/**
 * Task graph model: pure functions over template and workflow graphs.
 *
 * @typedef {'done'|'running'|'waiting'|'failed'|'notrun'} StatusKey
 * @typedef {Object} Step
 * @property {string} key
 * @property {string} title
 * @property {string} summary
 * @property {'TOOL'|'PROMPT'|'CODE'|'FLOW'} type
 * @property {boolean} isCheck
 * @property {boolean} checkAfter
 * @property {string} prompt
 * @property {string} model
 * @property {string[]} tools
 * @property {StatusKey} status
 * @typedef {Object} TaskGraph
 * @property {Step[]} steps
 * @property {{from: string, to: string}[]} edges
 */

const STATUS = {
  completed: 'done',
  executing: 'running',
  running: 'running',
  waiting_approval: 'waiting',
  paused: 'waiting',
  paused_cost_limit: 'waiting',
  payment_required: 'waiting',
  failed: 'failed',
  cancelled: 'failed',
};

const TYPE = {
  tool_execution: 'TOOL',
  image_generation: 'TOOL',
  data_retrieval: 'TOOL',
  output: 'TOOL',
  code_execution: 'CODE',
  human_approval: 'CHECK',
};

/** @returns {StatusKey} */
export function statusKey(raw) {
  return STATUS[raw] || 'notrun';
}

/** @returns {'TOOL'|'PROMPT'|'CODE'|'FLOW'|'CHECK'} */
export function stepTypeFor(nodeType) {
  return TYPE[nodeType] || 'PROMPT';
}

const byPosition = (a, b) =>
  (a.depth_level || 0) - (b.depth_level || 0) || (a.order_index || 0) - (b.order_index || 0);

function toStep(node, model, status, tools) {
  const type = stepTypeFor(node.node_type);
  return {
    key: node.node_key,
    title: node.display_name || node.node_key,
    summary: node.description || '',
    type,
    isCheck: type === 'CHECK',
    checkAfter: !!node.requires_approval,
    prompt: node.prompt_template || '',
    model,
    tools,
    status,
  };
}

function keepKnown(steps, edges) {
  const keys = new Set(steps.map((s) => s.key));
  return edges.filter((e) => keys.has(e.from) && keys.has(e.to));
}

/** @returns {TaskGraph} */
export function fromTemplateGraph(graphStructure) {
  const nodes = [...(graphStructure?.nodes || [])].sort(byPosition);
  const steps = nodes.map((n) =>
    toStep(n, n.suggested_model || '', 'notrun', n.required_tools || []));
  const edges = (graphStructure?.edges || [])
    .map((e) => ({ from: e.from_node_key, to: e.to_node_key }));
  return { steps, edges: keepKnown(steps, edges) };
}

/** @returns {TaskGraph} */
export function fromWorkflowGraph(graph) {
  const nodes = [...(graph?.nodes || [])].sort(byPosition);
  const keyById = {};
  nodes.forEach((n) => { keyById[n.id] = n.node_key; });
  const steps = nodes.map((n) =>
    toStep(n, n.selected_model || '', statusKey(n.status), []));
  const edges = (graph?.edges || [])
    .map((e) => ({ from: keyById[e.from_node_id], to: keyById[e.to_node_id] }));
  return { steps, edges: keepKnown(steps, edges) };
}

/** Kahn's algorithm; cycle leftovers go one per level in steps order. */
export function computeLevels(graph) {
  const keys = graph.steps.map((s) => s.key);
  const known = new Set(keys);
  const indeg = Object.fromEntries(keys.map((k) => [k, 0]));
  const out = Object.fromEntries(keys.map((k) => [k, []]));
  graph.edges.forEach(({ from, to }) => {
    if (!known.has(from) || !known.has(to)) return;
    out[from].push(to);
    indeg[to] += 1;
  });
  const levels = [];
  const placed = new Set();
  let current = keys.filter((k) => indeg[k] === 0);
  while (current.length) {
    levels.push(current);
    current.forEach((k) => placed.add(k));
    const next = new Set();
    current.forEach((k) => out[k].forEach((t) => {
      indeg[t] -= 1;
      if (indeg[t] === 0) next.add(t);
    }));
    current = keys.filter((k) => next.has(k));
  }
  keys.filter((k) => !placed.has(k)).forEach((k) => levels.push([k]));
  return levels;
}

export function numberSteps(graph, levels) {
  const isCheck = Object.fromEntries(graph.steps.map((s) => [s.key, s.isCheck]));
  const numbers = {};
  let n = 0;
  levels.flat().forEach((k) => {
    if (!isCheck[k]) { n += 1; numbers[k] = n; }
  });
  return numbers;
}

export function parallelSummary(graph, levels) {
  const isCheck = Object.fromEntries(graph.steps.map((s) => [s.key, s.isCheck]));
  const sizes = levels.map((l) => l.filter((k) => !isCheck[k]).length);
  const n = sizes.reduce((a, b) => a + b, 0);
  const groups = sizes.filter((s) => s >= 2);
  const base = `${n} ${n === 1 ? 'step' : 'steps'}`;
  if (!groups.length) return base;
  const g = groups.length;
  const word = groups.every((s) => s === 2) ? 'pair' : 'group';
  return `${base} · ${g} ${word}${g === 1 ? '' : 's'} ${g === 1 ? 'runs' : 'run'} at the same time`;
}

/** The step and everything reachable downstream, with only the edges inside. */
export function subgraphFrom(graph, key) {
  const seen = new Set([key]);
  const stack = [key];
  while (stack.length) {
    const k = stack.pop();
    graph.edges.forEach((e) => {
      if (e.from === k && !seen.has(e.to)) { seen.add(e.to); stack.push(e.to); }
    });
  }
  return {
    steps: graph.steps.filter((s) => seen.has(s.key)),
    edges: graph.edges.filter((e) => seen.has(e.from) && seen.has(e.to)),
  };
}
